export function getClockHourBucket(date: Date): { start: Date; end: Date } {
  const start = new Date(date);
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setHours(start.getHours() + 1);
  return { start, end };
}

export function snapToHalfHour(date: Date): Date {
  const next = new Date(date);
  const minutes = next.getMinutes();
  if (minutes < 15) {
    next.setMinutes(0, 0, 0);
  } else if (minutes < 45) {
    next.setMinutes(30, 0, 0);
  } else {
    next.setHours(next.getHours() + 1);
    next.setMinutes(0, 0, 0);
  }
  return next;
}

export function wouldExceedCapacity(
  currentPaxInHour: number,
  incomingPax: number,
  maxPaxPerHour: number | null | undefined,
): boolean {
  if (maxPaxPerHour == null) return false;
  return currentPaxInHour + incomingPax > maxPaxPerHour;
}

function parseReservationTime(value?: string | null): { hours: number; minutes: number } | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

export function formatReservationTime(value?: string | null): string | null {
  const parsed = parseReservationTime(value);
  if (!parsed) return null;
  return `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
}

export function minutesFromReservationTime(value?: string | null): number | null {
  const parsed = parseReservationTime(value);
  if (!parsed) return null;
  return parsed.hours * 60 + parsed.minutes;
}

export function isWithinReservationHours(
  startsAt: Date,
  opensAt?: string | null,
  closesAt?: string | null,
): boolean {
  const open = minutesFromReservationTime(opensAt);
  const close = minutesFromReservationTime(closesAt);
  if (open == null && close == null) return true;
  if (open == null || close == null) return true;
  const minutes = startsAt.getHours() * 60 + startsAt.getMinutes();
  return minutes >= open && minutes <= close;
}

export function getReservationCalendarBounds(
  date: Date,
  opensAt?: string | null,
  closesAt?: string | null,
): { min?: Date; max?: Date } {
  const open = parseReservationTime(opensAt);
  const close = parseReservationTime(closesAt);
  if (!open || !close) return {};

  const min = new Date(date);
  min.setHours(open.hours, open.minutes, 0, 0);

  const max = new Date(date);
  max.setHours(close.hours, close.minutes, 0, 0);
  max.setMinutes(max.getMinutes() + 30);
  if (max.getDate() !== date.getDate()) {
    max.setTime(date.getTime());
    max.setHours(23, 59, 59, 0);
  }
  return { min, max };
}

export function clampToReservationHours(
  date: Date,
  opensAt?: string | null,
  closesAt?: string | null,
): Date {
  const snapped = snapToHalfHour(date);
  if (isWithinReservationHours(snapped, opensAt, closesAt)) return snapped;

  const open = parseReservationTime(opensAt);
  if (!open) return snapped;

  const openToday = new Date(snapped);
  openToday.setHours(open.hours, open.minutes, 0, 0);
  if (snapped < openToday) return openToday;

  const tomorrow = new Date(openToday);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

export function sumPaxInClockHour(
  reservations: Array<{ id: string; pax: number; startsAt: Date }>,
  date: Date,
  excludeId?: string,
): number {
  const { start, end } = getClockHourBucket(date);
  return reservations.reduce((sum, reservation) => {
    if (excludeId && reservation.id === excludeId) return sum;
    if (reservation.startsAt >= start && reservation.startsAt < end) {
      return sum + reservation.pax;
    }
    return sum;
  }, 0);
}
